/* eslint jsx-a11y/interactive-supports-focus: 'off' */
/* eslint jsx-a11y/no-autofocus: 'off' */
import React, { useState, useEffect, forwardRef, useRef } from 'react';
import { Button, ButtonGroup, FormGroup } from 'react-bootstrap';
import { CSSTransition } from 'react-transition-group';
import RootCloseWrapper from 'react-overlays/lib/RootCloseWrapper';
import PropTypes from 'prop-types';
import _ from 'lodash';

import List from './List';

import { filteredOptionsHelper } from '../helpers';
import { useWindowSize } from '../../../../utils/hooks/useWindowSize';

const ItemsList = forwardRef(
  (
    {
      listRef,
      rootCloseEvent,
      open,
      setValues,
      filteredOptions,
      values,
      inputRef,
      group,
      groupKey,
      handleFocus,
      onToggleClose,
      onSelectValue,
      setFilteredOptions,
      options,
      showOptionId,
      showLoader,
      menuWidth,
      selectedLimit,
      showNotify,
      toggleNotify,
    },
    ref,
  ) => {
    const menuRef = useRef(null);

    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const { width } = useWindowSize();

    const handleKeyDown = (e) => {
      if (e.keyCode === 40 || e.keyCode === 38) {
        e.preventDefault();
      }
    };

    const useDebounce = (value, delay) => {
      const [debouncedValue, setDebouncedValue] = useState(value);

      useEffect(() => {
        setIsLoading(true);
        const handler = setTimeout(() => {
          setDebouncedValue(value);
          setIsLoading(false);
        }, delay);
        return () => {
          clearTimeout(handler);
        };
      }, [value, delay]);

      return debouncedValue;
    };

    const debounceValue = useDebounce(inputValue, showLoader ? 1000 : 0);

    useEffect(() => {
      setFilteredOptions(filteredOptionsHelper(options, debounceValue, group, groupKey));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debounceValue, open]);

    useEffect(() => {
      if (!open) {
        setInputValue('');
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    return (
      <RootCloseWrapper disabled={!open} onRootClose={onToggleClose} event={rootCloseEvent}>
        <div
          ref={ref}
          onClick={() => handleFocus()}
          role="menu"
          style={{ width: `${width < 535 ? width - 70 : menuWidth}px` }}
          className="dropdown-menu dropdown-menu__filters"
        >
          <div ref={menuRef}>
            <FormGroup className="dropdown-menu__input">
              <input
                className="form-control"
                ref={inputRef}
                autoFocus
                autoComplete="off"
                type="text"
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                value={inputValue}
                onKeyPress={(e) => {
                  if (e.which === 13 || e.keyCode === 13) {
                    e.preventDefault();
                  }
                }}
              />
            </FormGroup>
            <ButtonGroup className="dropdown-menu__btn-group" bsSize="small">
              <Button
                onClick={() => setValues(
                  _.union(
                    values.filter(Number),
                    filteredOptions?.filter((item) => !item.isDisabled && !item.isGroupHeader).map((item) => item.id),
                  ),
                )}
              >
                Select All
              </Button>
              <Button
                onClick={() => {
                  if (values.length > 0) {
                    setValues(
                      _.difference(
                        values.filter(Number),
                        filteredOptions?.map((item) => item.id),
                      ),
                    );
                  }
                }}
              >
                Deselect All
              </Button>
            </ButtonGroup>

            <div style={{ position: 'relative' }}>
              {isLoading && showLoader ? <div className="modal-loader-small" /> : null}
              <List
                filteredOptions={filteredOptions}
                options={options}
                listRef={listRef}
                onSelectValue={onSelectValue}
                values={values}
                isLoading={isLoading}
                screenWidth={width}
                showOptionId={showOptionId}
                menuRef={menuRef}
                group={group}
                groupKey={groupKey}
              />

              {filteredOptions.length === 0 && (
                <div className="menu-item-no_result">
                  <a href="# " onClick={(e) => e.preventDefault()}>
                    No results matched
                    {' '}
                    {inputValue && `"${inputValue}"`}
                  </a>
                </div>
              )}

              <CSSTransition
                in={showNotify}
                timeout={{
                  appear: 0,
                  enter: 0,
                  exit: 500,
                }}
                classNames="notify"
                unmountOnExit
                onEnter={() => toggleNotify()}
              >
                <div className="list-notify">
                  <div className="list-notify_text">
                    Please select all or up to
                    {selectedLimit}
                    {' '}
                    services
                  </div>
                </div>
              </CSSTransition>
            </div>
          </div>
        </div>
      </RootCloseWrapper>
    );
  },
);

ItemsList.propTypes = {
  listRef: PropTypes.object,
  inputRef: PropTypes.object,
  rootCloseEvent: PropTypes.string,
  open: PropTypes.bool,
  setValues: PropTypes.func,
  filteredOptions: PropTypes.array,
  values: PropTypes.array,
  handleFocus: PropTypes.func,
  onToggleClose: PropTypes.func,
  onSelectValue: PropTypes.func,
  setFilteredOptions: PropTypes.func,
  options: PropTypes.array,
  showOptionId: PropTypes.bool,
  showLoader: PropTypes.bool,
  menuWidth: PropTypes.number,
  selectedLimit: PropTypes.number,
  showNotify: PropTypes.bool,
  toggleNotify: PropTypes.func,
  group: PropTypes.bool,
  groupKey: PropTypes.string,
};

export default ItemsList;
